type AnalyticsEvent = 'page_view' | 'tool_attempt' | 'tool_success';
type AnalyticsSource =
  | 'direct'
  | 'google'
  | 'bing'
  | 'facebook'
  | 'ai'
  | 'other';

const ENDPOINT = 'https://receipt.bucksimple.com/api/analytics';
const SOURCE_PARAMETER = 'bs_source';
const SOURCE_STORAGE_KEY = 'bucksimple-acquisition-source';
const SOURCES = new Set<AnalyticsSource>([
  'direct',
  'google',
  'bing',
  'facebook',
  'ai',
  'other',
]);

function hostnameMatches(hostname: string, domain: string): boolean {
  return hostname === domain || hostname.endsWith(`.${domain}`);
}

function isGoogleHostname(hostname: string): boolean {
  const base = hostname.replace(/^(?:www|news|maps|accounts|m)\./, '');
  return /^google\.(?:com|[a-z]{2,3}|co\.[a-z]{2}|com\.[a-z]{2})$/.test(base);
}

function sourceForReferrer(): AnalyticsSource {
  if (!document.referrer) return 'direct';
  try {
    const hostname = new URL(document.referrer).hostname.toLowerCase();
    if (
      hostname === location.hostname.toLowerCase() ||
      hostnameMatches(hostname, 'bucksimple.com') ||
      hostname.endsWith('.rob243486.chatgpt.site')
    )
      return 'direct';
    if (
      hostnameMatches(hostname, 'chatgpt.com') ||
      hostnameMatches(hostname, 'perplexity.ai') ||
      hostnameMatches(hostname, 'claude.ai') ||
      hostnameMatches(hostname, 'copilot.microsoft.com') ||
      hostnameMatches(hostname, 'gemini.google.com') ||
      hostnameMatches(hostname, 'meta.ai') ||
      hostnameMatches(hostname, 'you.com')
    )
      return 'ai';
    if (
      isGoogleHostname(hostname) ||
      hostnameMatches(hostname, 'googleadservices.com')
    )
      return 'google';
    if (hostnameMatches(hostname, 'bing.com')) return 'bing';
    if (
      hostnameMatches(hostname, 'facebook.com') ||
      hostnameMatches(hostname, 'fb.com') ||
      hostnameMatches(hostname, 'messenger.com')
    )
      return 'facebook';
    return 'other';
  } catch {
    return 'other';
  }
}

function acquisitionSource(): AnalyticsSource {
  try {
    const url = new URL(location.href);
    const parameter = url.searchParams.get(SOURCE_PARAMETER);
    if (parameter && SOURCES.has(parameter as AnalyticsSource)) {
      const source = parameter as AnalyticsSource;
      sessionStorage.setItem(SOURCE_STORAGE_KEY, source);
      url.searchParams.delete(SOURCE_PARAMETER);
      history.replaceState(
        history.state,
        '',
        `${url.pathname}${url.search}${url.hash}`,
      );
      return source;
    }
    const stored = sessionStorage.getItem(SOURCE_STORAGE_KEY);
    if (stored && SOURCES.has(stored as AnalyticsSource))
      return stored as AnalyticsSource;
  } catch {
    // Storage and history are optional; referrer classification still works.
  }
  return sourceForReferrer();
}

function collectionAllowed(): boolean {
  const browser = navigator as Navigator & { globalPrivacyControl?: boolean };
  if (browser.webdriver || browser.globalPrivacyControl === true) return false;
  const doNotTrack = String(
    browser.doNotTrack ??
      (globalThis as typeof globalThis & { doNotTrack?: string }).doNotTrack ??
      '',
  ).toLowerCase();
  return doNotTrack !== '1' && doNotTrack !== 'yes';
}

let sessionSource: AnalyticsSource | null = null;

export function trackAnalytics(
  event: AnalyticsEvent,
  target: string,
): void {
  if (!collectionAllowed()) return;
  sessionSource ||= acquisitionSource();
  void fetch(ENDPOINT, {
    method: 'POST',
    mode: 'cors',
    credentials: 'omit',
    cache: 'no-store',
    keepalive: true,
    referrerPolicy: 'no-referrer',
    headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
    body: JSON.stringify({ event, target, source: sessionSource }),
  }).catch(() => undefined);
}
